import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [allWeeks, setAllWeeks] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customerName: '',
    customerWhatsapp: '',
    paymentMethod: 'pix'
  });

  // Generate all possible weeks (current + future weeks)
  useEffect(() => {
    const generateAllWeeks = () => {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1); // Get Monday of current week

      const weeks = [];
      // Generate enough weeks for navigation (current + 10 future weeks)
      for (let i = 0; i < 11; i++) {
        const weekStart = new Date(monday);
        weekStart.setDate(monday.getDate() + (i * 7));
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 5); // Saturday

        weeks.push({
          start: weekStart.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0],
          index: i
        });
      }
      
      setAllWeeks(weeks);
      setSelectedWeek(weeks[0]); // Default to current week (index 0)
    };

    generateAllWeeks();
  }, []);

  // Fetch schedule data when week is selected
  useEffect(() => {
    if (selectedWeek) {
      fetchSchedule(selectedWeek.start);
    }
  }, [selectedWeek]);

  // Handle week navigation
  const navigateToWeek = (direction) => {
    let newIndex;
    if (direction === 'next') {
      newIndex = currentWeekIndex + 1;
    } else if (direction === 'prev') {
      newIndex = currentWeekIndex - 1;
    } else {
      newIndex = direction; // direct index
    }

    if (newIndex >= 0 && newIndex < allWeeks.length) {
      setCurrentWeekIndex(newIndex);
      setSelectedWeek(allWeeks[newIndex]);
    }
  };

  // Get current navigation buttons based on current week index
  const getNavigationButtons = () => {
    if (currentWeekIndex === 0) {
      // Current week - show "Esta semana" and "Próxima semana"
      return [
        { label: 'Esta semana', action: () => navigateToWeek(0), active: true },
        { label: 'Próxima semana', action: () => navigateToWeek('next'), active: false }
      ];
    } else {
      // Any other week - show "Semana anterior" and "Próxima semana"
      return [
        { label: 'Semana anterior', action: () => navigateToWeek('prev'), active: false },
        { label: 'Próxima semana', action: () => navigateToWeek('next'), active: true, disabled: currentWeekIndex >= allWeeks.length - 1 }
      ];
    }
  };

  const fetchSchedule = async (weekStart) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/week-schedule/${weekStart}`);
      setScheduleData(response.data);
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotClick = (date, timeSlot, slotInfo) => {
    if (!slotInfo.available) return;
    
    setSelectedSlot({ date, timeSlot, ...slotInfo });
    setShowBookingForm(true);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingForm.customerName || !bookingForm.customerWhatsapp) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setLoading(true);
      
      const bookingData = {
        customer_name: bookingForm.customerName,
        customer_whatsapp: bookingForm.customerWhatsapp,
        date: selectedSlot.date,
        time_slot: selectedSlot.timeSlot,
        payment_method: bookingForm.paymentMethod
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      
      alert('Agendamento realizado com sucesso! Você receberá uma confirmação no WhatsApp.');
      
      // Reset form and close modal
      setShowBookingForm(false);
      setSelectedSlot(null);
      setBookingForm({
        customerName: '',
        customerWhatsapp: '',
        paymentMethod: 'pix'
      });
      
      // Refresh schedule
      if (selectedWeek) {
        fetchSchedule(selectedWeek.start);
      }
      
    } catch (error) {
      console.error('Erro ao realizar agendamento:', error);
      alert('Erro ao realizar agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return `${days[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`;
  };

  const timeSlots = ['17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];

  return (
    <div className="booking-page">
      {/* Header with Logo */}
      <header className="app-header">
        <div className="logo-container">
          <img 
            src="/arena-jungle-logo.png" 
            alt="Arena Jungle" 
            data-testid="arena-logo"
          />
        </div>
      </header>

      {/* Slideshow Section */}
      <div className="slideshow-container">
        <div className="slideshow-content">
          <div className="slideshow-placeholder" data-testid="slideshow-placeholder">
            📢 Área para avisos, promoções e patrocinadores
          </div>
        </div>
      </div>

      <div className="main-content">
        <h1 className="page-title" data-testid="page-title">Agende agora</h1>

        {/* Week Selection */}
        <div className="week-selector" data-testid="week-selector">
          {getNavigationButtons().map((button, index) => (
            <button
              key={index}
              className={`week-button ${button.active ? 'active' : ''}`}
              onClick={button.action}
              disabled={button.disabled}
              data-testid={`week-button-${index}`}
            >
              {button.label}
            </button>
          ))}
        </div>

        {/* Schedule Table */}
        {loading && (
          <div className="loading" data-testid="loading">
            Carregando horários...
          </div>
        )}

        {scheduleData && !loading && (
          <div className="schedule-container">
            <table className="schedule-table" data-testid="schedule-table">
              <thead>
                <tr>
                  <th>Horário</th>
                  {Object.keys(scheduleData.schedule).map(date => (
                    <th key={date}>{formatDate(date)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeSlots.map(timeSlot => (
                  <tr key={timeSlot}>
                    <td className="time-cell">{timeSlot}</td>
                    {Object.entries(scheduleData.schedule).map(([date, daySchedule]) => {
                      const slotInfo = daySchedule[timeSlot];
                      return (
                        <td
                          key={`${date}-${timeSlot}`}
                          className={slotInfo.available ? 'slot-available' : 'slot-unavailable'}
                          onClick={() => handleSlotClick(date, timeSlot, slotInfo)}
                          data-testid={`slot-${date}-${timeSlot}`}
                        >
                          <div className="slot-content">
                            {slotInfo.available ? (
                              <div>Disponível</div>
                            ) : (
                              <div>Indisponível</div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Price Information - moved after table */}
            <div className="price-info-section-bottom" data-testid="price-info">
              <div className="price-item-small">17H e 18H R$125,00</div>
              <div className="price-item-small">19H e 22H R$140,00</div>
              <div className="price-item-small">20H e 21H R$150,00</div>
              <div className="price-item-small card-price">Qualquer horário no Cartão R$160,00</div>
            </div>

            <div className="back-button">
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedWeek(null)}
                data-testid="back-button"
              >
                Voltar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingForm && selectedSlot && (
        <div className="booking-modal" data-testid="booking-modal">
          <form className="booking-form" onSubmit={handleBookingSubmit}>
            <h2 className="form-title">Finalizar Agendamento</h2>
            
            <div className="form-group">
              <label className="form-label">Data e Horário:</label>
              <div style={{ padding: '12px', background: '#374151', borderRadius: '8px' }}>
                {formatDate(selectedSlot.date)} - {selectedSlot.timeSlot}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="customerName">Nome *</label>
              <input
                type="text"
                id="customerName"
                className="form-input"
                value={bookingForm.customerName}
                onChange={(e) => setBookingForm({...bookingForm, customerName: e.target.value})}
                required
                data-testid="customer-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="customerWhatsapp">WhatsApp *</label>
              <input
                type="tel"
                id="customerWhatsapp"
                className="form-input"
                placeholder="(11) 99999-9999"
                value={bookingForm.customerWhatsapp}
                onChange={(e) => setBookingForm({...bookingForm, customerWhatsapp: e.target.value})}
                required
                data-testid="customer-whatsapp-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Forma de Pagamento:</label>
              <div className="payment-options">
                <label className={`payment-option ${bookingForm.paymentMethod === 'pix' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="pix"
                    checked={bookingForm.paymentMethod === 'pix'}
                    onChange={(e) => setBookingForm({...bookingForm, paymentMethod: e.target.value})}
                    data-testid="payment-pix"
                  />
                  <div className="payment-info">
                    <div className="payment-method">PIX</div>
                    <div className="payment-price">R$ {selectedSlot.price_pix?.toFixed(2)}</div>
                    <div className="payment-discount">Com desconto</div>
                  </div>
                </label>
                
                <label className={`payment-option ${bookingForm.paymentMethod === 'credit_card' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="credit_card"
                    checked={bookingForm.paymentMethod === 'credit_card'}
                    onChange={(e) => setBookingForm({...bookingForm, paymentMethod: e.target.value})}
                    data-testid="payment-credit"
                  />
                  <div className="payment-info">
                    <div className="payment-method">Cartão de Crédito</div>
                    <div className="payment-price">R$ {selectedSlot.price_card?.toFixed(2)}</div>
                  </div>
                </label>
                
                <label className={`payment-option ${bookingForm.paymentMethod === 'debit_card' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="debit_card"
                    checked={bookingForm.paymentMethod === 'debit_card'}
                    onChange={(e) => setBookingForm({...bookingForm, paymentMethod: e.target.value})}
                    data-testid="payment-debit"
                  />
                  <div className="payment-info">
                    <div className="payment-method">Cartão de Débito</div>
                    <div className="payment-price">R$ {selectedSlot.price_card?.toFixed(2)}</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Booking Rules */}
            <div className="booking-rules">
              <div className="rules-title">Regras de Agendamento:</div>
              <ul className="rules-list">
                <li>Agendamento somente após o pagamento</li>
                <li>Não haverá reembolso de pagamento de horários</li>
                <li>O horário poderá ser remarcado em até 3h antes do jogo</li>
                <li>Em casos de fatores externos como chuva ou falta de energia, o horário será remarcado onde poderá haver um tempo extra</li>
              </ul>
            </div>

            <div className="button-group">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowBookingForm(false);
                  setSelectedSlot(null);
                }}
                data-testid="cancel-booking-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading}
                data-testid="confirm-booking-button"
              >
                {loading ? 'Processando...' : 'Confirmar Agendamento'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookingPage;